import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Navbar, Icon, Button, Card, CarInfoBox, Badge, PageHeader, LinkButton, SectionHeader,
  GuestInfoSection, OptionsSection, TourScheduleSection,
  StickyBottomBar, PriceDisplay,
  ModalWrapper, ModalCloseButton,
} from '../../../components/common';
import PaymentMethodCard from '../../../components/service/PaymentMethodCard';
import PaymentAgreementCard from '../../../components/service/PaymentAgreementCard';
import { RESERVATION_STEPS, TOTAL_RESERVATION_STEPS } from '../../../constants/reservationSteps';
import { paymentMethods } from '../../../constants/uiConstants';
import EasyPayProviderModal from '../../../components/service/EasyPayProviderModal';
import { executePayment, isPortOneReady } from '../../../api/portone';
import { tourService, pricingService, reservationService } from '../../../api/services';
import { useMoney } from '../../../hooks/useMoney';
import { useAvailabilityCheck } from '../../../hooks/useAvailabilityCheck';
import useAlertModal from '../../../hooks/useAlertModal';
import { useDeepLTranslated } from '../../../hooks/useDeepLTranslated';
import { translateApiError, appendErrorReason } from '../../../utils/apiError';

/* ===== 프로모(쿠폰) 코드 입력 ===== */
function PromoCodeInput({ appliedCoupon, onApply, onRemove }) {
  const { t } = useTranslation();
  const money = useMoney();
  const [code, setCode] = useState('');
  const [error, setError] = useState('');

  const handleApply = async () => {
    if (!code.trim()) return;
    setError('');
    try {
      const result = await pricingService.validateCoupon(code);
      if (result.valid) {
        onApply({
          code: code.trim().toUpperCase(),
          name: result.name,
          discount_value: Number(result.discount_value),
          discount_type: result.discount_type,
        });
      } else {
        setError(result.message || t('payment.alert_invalid_code'));
      }
    } catch (e) {
      console.error('[Coupon] API 호출 에러:', e);
      setError(t('payment.alert_check_code_failed'));
    }
  };

  const handleRemove = () => {
    setCode('');
    setError('');
    onRemove();
  };

  return (
    <div className="space-y-2">
      {!appliedCoupon ? (
        <div className="flex gap-2">
          <input
            type="text"
            value={code}
            onChange={(e) => { setCode(e.target.value); setError(''); }}
            placeholder={t('payment.discount_code_placeholder')}
            className="flex-1 h-10 px-3 border border-mono-200 rounded-lg text-sm text-mono-900 placeholder:text-mono-400 focus:outline-none focus:border-orange-500 transition uppercase"
          />
          <Button level="level1" onClick={handleApply} className="shrink-0 h-10">{t('payment.apply')}</Button>
        </div>
      ) : (
        <div className="bg-orange-50 rounded-lg p-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Icon name="coupon" size={18} className="text-orange-500" />
            <span className="text-sm font-bold text-mono-900">{appliedCoupon.name}</span>
            <span className="text-sm font-bold text-orange-500">
              {appliedCoupon.discount_type === 'percentage'
                ? `-${appliedCoupon.discount_value}%`
                : `-${money(appliedCoupon.discount_value)}`}
            </span>
          </div>
          <button onClick={handleRemove} className="text-mono-400 hover:text-mono-600">
            <Icon name="close" size={16} />
          </button>
        </div>
      )}
      {error && <p className="text-xs text-red-500">{error}</p>}
    </div>
  );
}

export default function TourPayment() {
  const { t } = useTranslation();
  const money = useMoney();
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  // 모바일 결제 redirect(취소 복귀 포함) 시 history state 가 소실되므로 sessionStorage 에 캐시·복원.
  //   (TransferPayment 와 동일 패턴) → 결제 취소 후 결제 페이지로 돌아와도 입력값/예약이 그대로 유지.
  const paymentCacheKey = `vvt_payment_tour_${id}`;
  const cachedPayload = useMemo(() => {
    if (location.state?.bookingData || location.state?.reservationId) return null;
    try { const raw = sessionStorage.getItem(paymentCacheKey); if (raw) return JSON.parse(raw); } catch (_) {}
    return null;
  }, [location.state, paymentCacheKey]);

  const locationData = location?.state?.bookingData || cachedPayload?.bookingData || null;

  useEffect(() => {
    if (location.state?.bookingData || location.state?.reservationId) {
      try {
        sessionStorage.setItem(paymentCacheKey, JSON.stringify({
          bookingData: location.state.bookingData || null,
          reservationId: location.state.reservationId || null,
        }));
      } catch (_) {}
    }
  }, [location.state, paymentCacheKey]);

  // state·캐시 양쪽 모두 없을 때만 랜딩으로 리다이렉트(직접 URL 접근 차단).
  useEffect(() => {
    if (!locationData && !(location.state?.reservationId || cachedPayload?.reservationId)) {
      navigate(`/tour/${id}`, { replace: true });
    }
  }, [locationData, location.state, cachedPayload, navigate, id]);

  const { check: checkAvailability, modal: availabilityModal, showConflict } = useAvailabilityCheck({
    serviceType: 'tour',
    dateTime: locationData?.dateTime || '',
    passengers: locationData?.peopleQty || 1,
    onSelectOtherDate: () => navigate(-1),
  });

  const [data, setData] = useState(locationData || {
    title: '', dateTime: '', vehicle: '', baseHours: 2,
    reserverName: '', phone: '', email: '', peopleQty: 4,
    departure: '', stopovers: [], arrival: '',
    originalPrice: 0,
    surchargeText: '',
    surchargeOvertimePrice: 0,
  });
  const [vehicleSpecs, setVehicleSpecs] = useState({});

  useEffect(() => {
    const loadData = async () => {
      try {
        // 어드민 변경(기본가격·할증·옵션·투어 정보)을 결제 단계에서도 실시간 반영 —
        // 부킹 스냅샷에 의존하지 않고 매 로드 시 투어/할증/가격을 라이브 재조회한다.
        // 가격은 서버 calculate_price(=예약생성과 동일)인 /api/price/search 로 단일화해 표시=청구 일치.
        const payload = locationData?.reservationPayload;
        const priceReq = payload ? {
          service_type: 'tour',
          // 투어 base 가격은 destination_region(투어명)으로 조회됨 — 누락 시 디폴트(350000) 폴백되므로 필수.
          destination_region: payload.destination_region || data.title || undefined,
          date: (payload.pickup_date || '').split('T')[0] || undefined,
          pickup_time: payload.pickup_time || undefined,
          passenger_count: payload.passenger_count || 1,
          selected_options: (payload.options || []).map((o) => ({ id: o.option_id, quantity: o.quantity })),
          stopover_count: (payload.stopovers || []).length,
          tour_id: Number(id) || undefined,
        } : null;
        const [tourData, vehicleData, surchargeData, priceData] = await Promise.all([
          tourService.getById(id).catch(() => null),
          pricingService.getVehicleInfo('tour'),
          pricingService.getServiceSurcharges('tour'),
          priceReq ? pricingService.search(priceReq).catch(() => null) : Promise.resolve(null),
        ]);
        setVehicleSpecs(vehicleData);
        setData((prev) => ({
          ...prev,
          title: tourData?.name ?? prev.title,
          vehicle: vehicleData.carName || prev.vehicle,
          baseHours: tourData?.duration_hours ?? prev.baseHours,
          originalPrice: priceData?.base_price != null ? Number(priceData.base_price)
            : (tourData?.price != null ? Number(tourData.price) : prev.originalPrice),
          surchargeText: surchargeData.overtime || prev.surchargeText,
          // #272: 영어 등 다국어 환경에서 초과요금이 KRW 문자열로 노출되던 문제 → overtime_price 로 useMoney 환산
          surchargeOvertimePrice: Number(surchargeData.overtime_price) || prev.surchargeOvertimePrice,
          ...(priceData?.total != null ? { subtotal: Number(priceData.total) } : {}),
        }));
      } catch (err) {
        console.warn('TourPayment 데이터 로드 실패:', err.message);
      }
    };
    loadData();
  }, [id, locationData]);

  // BE 가 한국어로 내려주는 투어명/차량명/차량 스펙/일정 주소 를 현재 UI 언어로 DeepL 번역.
  // i18n key 가 없는 동적 콘텐츠 (어드민이 입력한 임의 문자열) 이라 DeepL fallback 사용.
  const localizableBundle = useMemo(() => {
    const bag = [];
    if (data.title) bag.push(data.title);
    if (data.vehicle) bag.push(data.vehicle);
    if (data.surchargeText) bag.push(data.surchargeText);
    if (vehicleSpecs.seat) bag.push(vehicleSpecs.seat);
    if (vehicleSpecs.capacity) bag.push(vehicleSpecs.capacity);
    if (vehicleSpecs.luggage) bag.push(vehicleSpecs.luggage);
    if (data.departure) bag.push(data.departure);
    if (data.departureName) bag.push(data.departureName);
    if (data.departureNotes) bag.push(data.departureNotes);
    if (data.arrival) bag.push(data.arrival);
    if (data.arrivalName) bag.push(data.arrivalName);
    (data.stopovers || []).forEach((s) => {
      if (s.address) bag.push(s.address);
      if (s.name) bag.push(s.name);
    });
    return bag;
  }, [data.title, data.vehicle, data.surchargeText, vehicleSpecs, data.departure, data.departureName, data.departureNotes, data.arrival, data.arrivalName, data.stopovers]);
  const localizedList = useDeepLTranslated(localizableBundle);
  const tx = useMemo(() => {
    const m = new Map();
    localizableBundle.forEach((src, i) => { m.set(src, localizedList[i] || src); });
    return (s) => (typeof s === 'string' ? (m.get(s) || s) : s);
  }, [localizableBundle, localizedList]);
  const translatedStopovers = useMemo(() => (data.stopovers || []).map((s) => ({
    ...s,
    address: tx(s.address),
    name: tx(s.name),
  })), [data.stopovers, tx]);

  // 예약 대기에서 직접 진입한 경우와 결제 시점에 새로 생성한 경우를 통합 관리
  const incomingReservationId = location.state?.reservationId || cachedPayload?.reservationId || null;
  const [createdReservationId, setCreatedReservationId] = useState(incomingReservationId);
  const reservationId = createdReservationId;

  /* 결제 / 약관 / 쿠폰 상태 */
  // 기본 선택은 card. simple(간편결제)은 2026-06-09 재가맹으로 옵션 복원됐으나 기본값은 card 유지.
  const [paymentMethod, setPaymentMethod] = useState('card');
  const [easyPayOpen, setEasyPayOpen] = useState(false);
  const [paying, setPaying] = useState(false);
  const { showAlert, alertModal } = useAlertModal();
  const [agreeAll, setAgreeAll] = useState(false);
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [showCouponLimitModal, setShowCouponLimitModal] = useState(false);

  // #233: 결제 합산은 base(originalPrice) 가 아니라 booking 에서 넘어온 subtotal
  // (추가옵션 + 추가인원 + 주말/야간 할증 포함) 기준. subtotal 누락(직접 진입) 시 base 폴백.
  const subtotal = data.subtotal ?? data.originalPrice ?? 0;
  // 쿠폰 할인액 계산 (FE 표시용)
  const couponDiscountAmount = appliedCoupon
    ? (appliedCoupon.discount_type === 'percentage'
        ? Math.round(subtotal * appliedCoupon.discount_value / 100)
        : Math.min(appliedCoupon.discount_value, subtotal))
    : 0;
  const displayPrice = Math.max(0, subtotal - couponDiscountAmount);

  const handleApplyCoupon = (coupon) => {
    // 쿠폰은 1건만 사용 가능 — 이미 적용된 쿠폰이 있으면 모달 노출
    if (appliedCoupon) {
      setShowCouponLimitModal(true);
      return;
    }
    setAppliedCoupon(coupon);
    if (!incomingReservationId) {
      setCreatedReservationId(null);
    }
  };
  const handleRemoveCoupon = () => {
    setAppliedCoupon(null);
    if (!incomingReservationId) {
      setCreatedReservationId(null);
    }
  };

  const handlePayment = async (methodOverride) => {
    if (!agreeAll) {
      showAlert(t('payment.alert_terms_required'));
      return;
    }
    const available = await checkAvailability();
    if (!available) return;
    if (!isPortOneReady()) {
      showAlert(t('payment.alert_payment_module_not_ready'));
      return;
    }
    // 간편결제(simple): 결제수단 선택 모달을 띄우고, 모달에서 고른 provider 로 재호출.
    const method = (typeof methodOverride === 'string' ? methodOverride : null) || paymentMethod;
    if (method === 'simple') { setEasyPayOpen(true); return; }

    setPaying(true);
    window.scrollTo(0, 0);
    try {
      // 결제 대상 reservation 확보 — 없으면 payload + 쿠폰 코드로 신규 생성
      let payReservationId = reservationId;
      if (!payReservationId) {
        const payload = locationData?.reservationPayload;
        if (!payload) {
          console.error('[Payment] reservationPayload 없음 — 결제 차단');
          showAlert(t('payment.alert_no_reservation'));
          setPaying(false);
          return;
        }
        try {
          const created = await reservationService.create({
            ...payload,
            ...(appliedCoupon ? { coupon_code: appliedCoupon.code } : {}),
          });
          if (process.env.NODE_ENV !== 'production') {
            console.log('[Payment] reservation 생성 완료:', { id: created.id, total_price: created.total_price, discount_amount: created.discount_amount });
          }
          payReservationId = created.id;
          setCreatedReservationId(created.id);
        } catch (err) {
          console.error('[Payment] reservation 생성 실패:', err);
          // BugHerd #4: 결제 직전 409 slot_conflict 는 alert 대신 가용성 모달과 동일 UX로.
          if (err?.code === 'slot_conflict') {
            showConflict?.();
          } else {
            // 그 외 코드(휴무일/2개월 룰 등)는 i18n 매핑 시도, 폴백은 BE detail.
            showAlert(translateApiError(err, t, 'booking.alert_reservation_failed'));
          }
          setPaying(false);
          return;
        }
      }

      const result = await executePayment({
        reservationId: payReservationId,
        paymentType: 'initial',
        paymentMethod: method,
        buyerName: data.userName || data.reserverName,
        buyerTel: data.phone,
        buyerEmail: data.email,
        serviceType: 'tour',
        redirectUrl: `${window.location.origin}/tour/${id}/complete?id=${payReservationId}`,
      });
      if (result?.status === 'ready') {
        navigate(`/tour/${id}/complete`, { state: { reservationId: payReservationId, vbank: result } });
      } else {
        navigate(`/tour/${id}/complete`, { state: { reservationId: payReservationId } });
      }
    } catch (err) {
      // 예약은 생성됐고 결제(executePayment) 단계 실패/취소 → 취소(info)와 실패(error)를 분리.
      // PG/서버가 내려준 사유(err.message)를 base 뒤에 "(사유: XXX)" 형태로 부착.
      const cancelled = err?.code === 'payment_cancelled' || err?.code === 'payment_not_completed';
      const base = translateApiError(err, t, cancelled ? 'payment.alert_payment_cancelled' : 'payment.alert_payment_failed');
      showAlert(
        appendErrorReason(base, err, t),
        { variant: cancelled ? 'info' : 'error' },
      );
    } finally {
      setPaying(false);
    }
  };

  return (
    <>
      <Navbar />
      <main className="bg-mono-50 min-h-screen pb-36 sm:pb-32">
        <div className="max-w-content mx-auto px-2.5 sm:px-5 py-5 flex flex-col gap-1 sm:gap-5">

          {/* ===== Header: Back + Title ===== */}
          {/* PageHeader 의 < 뒤로가기 버튼이 이미 booking 페이지 복귀를 담당해서 [수정] 우측 액션 중복 제거. */}
          <PageHeader title={t('payment.title')} />

          {/* ===== Service info ===== */}
          <Card>
            <div className="space-y-2">
              <Badge variant="service" serviceType="tour">{t('nav.private_tour')}</Badge>
              <h2 className="text-base font-bold text-mono-900">{t('nav.private_tour')} - {tx(data.title)}</h2>
              <p className="text-2sm text-mono-500">{t('payment.scheduled_use_time')}: {data.dateTime ? data.dateTime.replace('T', ' ') : '-'}</p>
            </div>
          </Card>

          {/* ===== 차량 정보 ===== */}
          <Card>
            <SectionHeader variant="card" title={t('booking.vehicle_info')} />
            <CarInfoBox
              images={vehicleSpecs.images || []}
              carName={tx(data.vehicle)}
              usageTimeBadge={t('form.base_time_hours', { hours: data.baseHours })}
              specs={[
                { icon: 'van', text: tx(vehicleSpecs.seat) || '' },
                { icon: 'person', text: tx(vehicleSpecs.capacity) || '' },
                { icon: 'bag', text: tx(vehicleSpecs.luggage) || '' },
              ]}
            >
              <div className="flex items-center gap-2">
                <Icon name="time" size={18} className="text-mono-500 shrink-0" />
                {/* #269: 가격 제거, 이용 시간만 표기 (예약조건 변동에 따른 불일치 방지) */}
                <span className="text-2sm font-medium text-mono-700">{t('form.usage_time_max', { hours: data.baseHours })}</span>
              </div>
              <div className="flex items-center gap-2">
                <Icon name="time" size={18} className="text-mono-500 shrink-0" />
                <span className="text-2sm font-medium text-mono-700">{data.surchargeOvertimePrice > 0
                  ? t('booking.overtime_fee_per_hour', { amount: money(data.surchargeOvertimePrice) })
                  : (tx(data.surchargeText) || '-')}</span>
              </div>
            </CarInfoBox>
          </Card>

          {/* ===== 예약자 정보 ===== */}
          <Card>
            <GuestInfoSection name={data.userName || data.reserverName} phone={data.phone} email={data.email} />
          </Card>

          {/* ===== 선택 옵션 ===== */}
          <Card>
            <OptionsSection options={[
              { label: t('tour.use_passengers'), value: `${data.peopleQty}${t('common.people')}`, price: `(+${money(0)})` },
              ...(data.selectedOptionItems || []), // #233: booking 에서 선택한 추가 옵션 반영
            ]} />
          </Card>

          {/* ===== 투어 일정 정보 ===== */}
          <Card>
            <TourScheduleSection
              departure={{ address: tx(data.departure), name: tx(data.departureName), time: data.dateTime, notes: tx(data.departureNotes || '') }}
              stopovers={translatedStopovers}
              arrival={{ address: tx(data.arrival), name: tx(data.arrivalName) }}
            />
          </Card>

          {/* ===== 프로모 코드 — 신규 예약 흐름에서만 노출 ===== */}
          {!incomingReservationId && (
            <Card>
              <SectionHeader variant="card" title={t('payment.discount_code')} />
              <PromoCodeInput
                appliedCoupon={appliedCoupon}
                onApply={handleApplyCoupon}
                onRemove={handleRemoveCoupon}
              />
            </Card>
          )}

          <PaymentMethodCard
            paymentMethods={paymentMethods}
            selected={paymentMethod}
            onSelect={setPaymentMethod}
          />

          <PaymentAgreementCard onAllAgreedChange={(v) => setAgreeAll(v)} />

        </div>
      </main>

      {/* ===== Sticky bottom bar ===== */}
      <StickyBottomBar step={RESERVATION_STEPS.payment} totalSteps={TOTAL_RESERVATION_STEPS}>
        <PriceDisplay
          approx
          label={t('payment.estimated_payment')}
          amount={displayPrice}
          size="sm"
          badge={appliedCoupon ? t('payment.discount_badge') : undefined}
          originalAmount={appliedCoupon ? subtotal : undefined}
        />
        <Button onClick={handlePayment} disabled={!agreeAll || paying} width="responsive-130">
          {paying ? t('payment.paying') : t('payment.pay_button')}
        </Button>
      </StickyBottomBar>
      {availabilityModal}
      {alertModal}
      <EasyPayProviderModal
        open={easyPayOpen}
        onClose={() => setEasyPayOpen(false)}
        onSelect={(providerId) => { setEasyPayOpen(false); handlePayment(providerId); }}
      />

      {/* ===== 쿠폰 1건 제한 안내 모달 ===== */}
      {showCouponLimitModal && (
        <ModalWrapper onClose={() => setShowCouponLimitModal(false)} variant="compact" gap="gap-4">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-bold text-mono-900">{t('payment.coupon_limit_title')}</h2>
            <ModalCloseButton onClick={() => setShowCouponLimitModal(false)} />
          </div>
          <p className="text-sm text-mono-700">
            {t('payment.coupon_limit_message')}
          </p>
          <Button level="blue" fullWidth onClick={() => setShowCouponLimitModal(false)}>
            {t('common.confirm')}
          </Button>
        </ModalWrapper>
      )}
    </>
  );
}
